import type * as React from "react"
import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ChangeEvent,
  type FocusEvent,
  type ReactNode,
} from "react"

import {
  capDecimals,
  formatAmount,
  parseAmount,
  rawAmount,
  roundToScale,
  separatorsFor,
} from "@repo/ui/lib/amount-format"
import { cn } from "@repo/ui/lib/utils"

import { Input, type InputProps } from "./input"
import { InputGroup, InputGroupAddon, InputGroupInput } from "./input-group"
import { OptionPicker } from "./option-picker"

/**
 * A monetary amount input: locale-formatted for reading, plain `number` for
 * storing.
 *
 * ## When to use
 *
 * Any field holding an amount of money. Use `DecimalInput` for a unitless
 * decimal (a distance, a multiplier) and `PercentageInput` for a rate stored as
 * a decimal.
 *
 * ## Display vs. value
 *
 * The locale affects the DISPLAYED string only. What reaches `onChange` — and
 * therefore the form, the request body and the column — is a plain `number`,
 * which carries no locale: someone typing `1.234,56` in de-DE and someone
 * typing `1,234.56` in en-GB submit the identical payload.
 *
 * Two independent knobs control precision:
 * - `scale` caps what the VALUE may carry, enforced as the user types.
 * - `displayDecimals` is cosmetic, applied to the blurred display only.
 *
 * They differ when a value was computed rather than typed (a tax split, an FX
 * conversion). Focusing the field then swaps the display for the full-precision
 * string, so an edit can never silently truncate digits the display had rounded
 * away.
 *
 * @example
 * ```tsx
 * <AmountInput
 *   value={total}
 *   onChange={setTotal}
 *   prefix={currencySymbol("ZAR")}
 * />
 * ```
 */
export type AmountUnit = { value: string; label: string; description?: string }

export interface AmountInputProps extends Omit<
  InputProps,
  "value" | "onChange" | "onBlur" | "onFocus" | "type" | "inputMode" | "prefix"
> {
  /**
   * Accepts a string as well as a number: a `numeric` column read through a
   * driver that maps it to a string still lands here as a valid amount.
   */
  value: number | string | null | undefined
  /** Receives the parsed number, or `null` once the field is cleared. */
  onChange: (value: number | null) => void
  onBlur?: () => void
  /** Currency symbol, word or icon shown in a leading addon. */
  prefix?: ReactNode
  /**
   * A fixed unit shown in a trailing addon — `%`, `days`, a currency code.
   * The same filled cap as `prefix`, on the other end.
   */
  suffix?: ReactNode
  /**
   * A unit the user chooses — a percentage or a sum, days or weeks. The cap
   * becomes the menu: the whole thing is the trigger, so it reads as one
   * control rather than a field with a button beside it. Takes precedence
   * over `suffix`.
   */
  units?: readonly AmountUnit[]
  unit?: string
  onUnitChange?: (unit: string) => void
  /** Decimal places the VALUE may carry; typing past it truncates. Default 2. */
  scale?: number
  /** Decimal places the blurred display shows. Default 2. */
  displayDecimals?: number
  /** Pins the display locale; defaults to the browser's after mount. */
  locale?: string
}

/**
 * The locale both sides of hydration agree on. The real locale is only known in
 * the browser, so rendering it on the server would mismatch — the same reason
 * `formatRateAsPercentage` pins one and `date-cell` renders client-only.
 */
const HYDRATION_LOCALE = "en-US"

function useAmountLocale(locale?: string): string {
  const [detected, setDetected] = useState<string>()

  useEffect(() => {
    if (!locale) setDetected(navigator.language)
  }, [locale])

  return locale ?? detected ?? HYDRATION_LOCALE
}

/**
 * Keeps only what can belong to an amount: digits, one decimal separator, and a
 * leading minus. Written as a scan rather than a regex because the separator is
 * a runtime value that would otherwise need escaping into a character class.
 */
function sanitizeAmount(input: string, decimal: string): string {
  let out = ""
  let seenDecimal = false
  for (const char of input) {
    if (char >= "0" && char <= "9") {
      out += char
    } else if (char === decimal && !seenDecimal) {
      out += char
      seenDecimal = true
    } else if (char === "-" && out.length === 0) {
      out += char
    }
  }
  return out
}

/** A `numeric` column arriving as a string still has to render as an amount. */
function toNumber(value: number | string | null | undefined): number | null {
  if (value == null || value === "") return null
  const parsed = typeof value === "number" ? value : Number(value)
  return Number.isFinite(parsed) ? parsed : null
}

export function AmountInput({
  value,
  onChange,
  onBlur,
  prefix,
  suffix,
  units,
  unit,
  onUnitChange,
  scale = 2,
  displayDecimals = 2,
  locale: localeProp,
  className,
  ...inputProps
}: AmountInputProps) {
  const locale = useAmountLocale(localeProp)
  const current = toNumber(value)

  const [focused, setFocused] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const [text, setText] = useState(() =>
    formatAmount(current, locale, displayDecimals),
  )

  // Re-sync from the value whenever the field is NOT being edited — a form
  // reset, a record loading, the locale resolving after mount. While focused the
  // typed string is the source of truth, so nothing reformats under the cursor.
  useEffect(() => {
    if (focused) return
    setText(formatAmount(current, locale, displayDecimals))
  }, [current, locale, displayDecimals, focused])

  // Swapping in the raw figure on focus drops the selection a Tab-in made, so
  // the next keystroke would append to the old amount instead of replacing
  // it. Re-selected in the same commit, before any key can land.
  const reselect = useRef(false)
  useLayoutEffect(() => {
    if (!focused || !reselect.current) return
    reselect.current = false
    inputRef.current?.select()
  }, [focused, text])

  const bindings = {
    ...inputProps,
    ref: inputRef,
    type: "text" as const,
    inputMode: "decimal" as const,
    // Amounts are read by magnitude, so the digits line up on a right edge with
    // tabular figures — the one input here that isn't left-aligned.
    className: cn("text-right tabular-nums", className),
    value: text,
    onChange: (e: ChangeEvent<HTMLInputElement>) => {
      const { decimal } = separatorsFor(locale)
      const next = capDecimals(
        sanitizeAmount(e.target.value, decimal),
        decimal,
        scale,
      )
      // Nothing re-renders when the sanitized string is unchanged, which would
      // leave a rejected character sitting in the DOM. Drop it directly.
      if (next !== e.target.value) e.target.value = next
      setText(next)

      const parsed = parseAmount(next, locale)
      onChange(parsed === null ? null : roundToScale(parsed, scale))
    },
    onFocus: (e: FocusEvent<HTMLInputElement>) => {
      const input = e.currentTarget
      reselect.current =
        input.value.length > 0 &&
        input.selectionStart === 0 &&
        input.selectionEnd === input.value.length
      setFocused(true)
      setText(rawAmount(current, locale))
    },
    onBlur: () => {
      setFocused(false)
      onBlur?.()
    },
  }

  // No addon means no group at all — an empty cap would still draw its border.
  if (!prefix && !suffix && !units) return <Input {...bindings} />

  return (
    // `InputGroup` defaults to the compact `h-8` its only other callers want —
    // the search row inside a picker popover. As a FORM control it has to match
    // the `Input` beside it in the grid, in height and in corner radius, or the
    // field visibly shrinks the moment it gains a prefix.
    <InputGroup className="rounded-input h-9">
      {/* Filled: the symbol reads as a fixed part of the control rather than
          as something typed into it. */}
      {prefix ? (
        <InputGroupAddon align="inline-start" variant="filled">
          {prefix}
        </InputGroupAddon>
      ) : null}
      <InputGroupInput {...bindings} />
      {units ? (
        <InputGroupAddon align="inline-end" variant="filled" className="p-0">
          <AmountUnitMenu
            units={units}
            unit={unit}
            onChange={onUnitChange}
            disabled={inputProps.disabled}
            returnFocusTo={inputRef}
          />
        </InputGroupAddon>
      ) : suffix ? (
        <InputGroupAddon align="inline-end" variant="filled">
          {suffix}
        </InputGroupAddon>
      ) : null}
    </InputGroup>
  )
}

/** The cap as the picker: every select's dropdown, so a long list (currencies)
 * searches and a short one (a percentage or a sum) does not. The trigger fills
 * the cap so the hover and focus rings are the cap's own. */
function AmountUnitMenu({
  units,
  unit,
  onChange,
  disabled,
  returnFocusTo,
}: {
  units: readonly AmountUnit[]
  unit?: string
  onChange?: (unit: string) => void
  disabled?: boolean
  /** Picking a unit is a step on the way to typing the figure, so the field
   * takes focus back after a pick. */
  returnFocusTo: React.RefObject<HTMLInputElement | null>
}) {
  const current = units.find((option) => option.value === unit) ?? units[0]
  return (
    <OptionPicker
      forceMode="select"
      searchable={units.length > 3}
      size="lg"
      options={units}
      value={current?.value}
      disabled={disabled}
      onValueChange={(value) => {
        if (!value) return
        onChange?.(value)
        requestAnimationFrame(() => returnFocusTo.current?.focus())
      }}
      className="text-muted-foreground hover:bg-hover hover:text-foreground w-auto gap-1 rounded-l-none border-0 bg-transparent px-2.5 py-0 text-sm font-medium focus-visible:ring-inset"
    />
  )
}
